import React, { useState, useEffect, useCallback } from 'react';
import Workspace from './components/Workspace';
import TopBar from './components/TopBar';
import RightSidebar from './components/RightSidebar';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import AdminPanel from './components/AdminPanel';
import KairiDashboard from './components/KairiDashboard';
import OnboardingWizard from './components/OnboardingWizard';
import './App.css';
import {
  ChakraProvider,
  useToast,
  extendTheme,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  ModalCloseButton,
  Button,
  Text,
  useDisclosure
} from "@chakra-ui/react";
import { jwtDecode } from 'jwt-decode';
import { getAllTransactions, getTransactionsSymbol, createTransaction, updateTransaction, deleteTransaction } from './api/transactions';
import { getAtivosOptions } from './api/default';
import { getUser } from './api/user';
import WebSocketManager from './components/WebSocketManager';
import translations from './util/translations';
import { initializeAnalytics, trackPageView } from "./components/analytics";
import ConsentimentoCookies from "./components/ConsentimentoCookies";
import ResponsiveSidebarLayout from './components/ResponsiveSidebarLayout';
import { useActivityLogger } from './hooks/useActivityLogger';
import { createUserLog } from './api/userLogs';
import OptionsGridPage from './pages/OptionsGridPage';
import { chamadaRemovida } from './config/api';

function App() {
  const [currentHash, setCurrentHash] = useState(window.location.hash);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [passwordResetToken, setPasswordResetToken] = useState(() => (
    new URLSearchParams(window.location.search).get('resetToken') || ''
  ));
  const [passwordResetComplete, setPasswordResetComplete] = useState(false);
  const [showLogin, setShowLogin] = useState(() => Boolean(passwordResetToken));
  const [userId, setUserId] = useState(null);
  const [selectedStock, setSelectedStock] = useState(null);

  const [selectedTab, setSelectedTab] = useState('chart');
  const [stocksWithOptions, setStocksWithOptions] = useState([]);
  const [stockSymbols, setStockSymbols] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [tickersWithOptions, setTickersWithOptions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [webSocketManager, setWebSocketManager] = useState(null);
  const [language, setLanguage] = useState('en');
  const [optionData, setOptionData] = useState([]); // Novo estado para armazenar as opções
  const [optionDataFull, setOptionDataFull] = useState([]); // Novo estado para armazenar as opções
  const [dueDateFilter, setDueDateFilter] = useState(null);
  const [stockData, setStockData] = useState({});
  const [loadingOptions, setLoadingOptions] = useState(false); // Novo estado para controlar o carregamento das opções
  const [nearestDueDate, setNearestDueDate] = useState(null);
  const [distinctDueDates, setDistinctDueDates] = useState([]);
  const [cliente, setCliente] = useState(1);
  const [lastPrice, setLastPrice] = useState(0);
  const [hasOption, setHasOption] = useState(0);
  const [renewDate, setRenewDate] = useState(null);
  const [selectTicker, setSelectTicker] = useState(null);
  const [descriptionCliente, setDescriptionCliente] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingEmail, setOnboardingEmail] = useState('');

  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    const WIDGET_ID = 'fatcat-chat-widget';
    const removeChatWidget = () => {
      const script = document.getElementById(WIDGET_ID);
      if (script) script.remove();
      document.querySelectorAll(
        '#chat-widget-container, [data-widget], .cw-wrap, .cw-c'
      ).forEach(element => element.remove());
      document.querySelectorAll('style').forEach(style => {
        if (style.textContent.includes('.cw-wrap') && style.textContent.includes('.cw-c')) {
          style.remove();
        }
      });
    };
    const isAllowedPage = isAuthChecked && !isAuthenticated && currentHash !== '#admin';
    let widgetObserver;

    if (isAllowedPage) {
      if (!document.getElementById(WIDGET_ID)) {
        const s = document.createElement('script');
        s.id = WIDGET_ID;
        s.async = true;
        s.src = 'https://prggcddwvgcvjhcgendu.supabase.co/functions/v1/chat-widget-loader?widget=aad0baf6-a106-42cb-80a9-381da33c1ea5&_v=20576';
        document.body.appendChild(s);
      }
    } else {
      removeChatWidget();
      widgetObserver = new MutationObserver(removeChatWidget);
      widgetObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => widgetObserver?.disconnect();
  }, [currentHash, isAuthenticated, isAuthChecked]);



  const toast = useToast();

  const config = {
    initialColorMode: "dark",
    useSystemColorMode: false,
  };

  const theme = extendTheme({ config });

  // const toggleChat = () => setIsChatOpen(!isChatOpen);

  const [idSessao] = useState(() => crypto.randomUUID());

  const handleLogin = async (token) => {
    setIsAuthenticated(true);
    setIsAuthChecked(true);
    const decodedToken = jwtDecode(token);
    localStorage.setItem('token', token);
    localStorage.setItem('userId', decodedToken.id);
    localStorage.setItem('firstAccess', decodedToken.firstAccess);
    localStorage.setItem('cliente', decodedToken.cliente);
    localStorage.setItem('username', decodedToken.username || '');
    setCliente(decodedToken.cliente);

    createUserLog({
      id_usuario: decodedToken.id,
      componente: 'Autenticacao',
      acao: 'login',
      caminho: window.location.pathname,
      id_sessao: idSessao
    });

    localStorage.setItem('idSessao', idSessao);

    setUserId(decodedToken.id);
    if (decodedToken.idioma) {
      setLanguage(decodedToken.idioma);
    }

    // Verifica se o onboarding precisa ser exibido
    const emailStr = localStorage.getItem('userEmail') || '';
    if (emailStr) {
      try {
        const r = await fetch('/api/clientes/onboarding-status', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json();
        if (d.found && !d.concluido) {
          setOnboardingEmail(emailStr);
          setShowOnboarding(true);
        }
      } catch (_) {}
    }
  };


  const handleWebSocketMessage = useCallback((priceData) => {
    setStockData((prevStockData) => {
      const updatedStockData = { ...prevStockData };

      priceData.forEach(({ ticker, value, variation }) => {
        if (ticker && typeof ticker === 'string') {
          const cleanTicker = ticker.replace('.SA', '');  // Remove o sufixo '.SA'

          // Verifica se o ativo já existe no stockData
          const existingData = updatedStockData[cleanTicker];

          // Se o ativo já existe, atualiza somente os campos de preço e volume
          if (existingData) {
            updatedStockData[cleanTicker] = {
              ...existingData, // Mantém os outros dados inalterados
              price: parseFloat(value),
              variation: parseFloat(variation)
            };
          } else {
            // Se o ativo ainda não existe, inicializa todos os campos
            updatedStockData[cleanTicker] = {
              symbol: cleanTicker,
              price: parseFloat(value),
              variation: parseFloat(variation)
            };
          }

        }
      });

      return updatedStockData;
    });
  }, []);


  const handleWebSocketError = useCallback((error) => {
    console.error('App: WebSocket error:', error);
  }, []);

  useEffect(() => {
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    let isActive = true;

    const clearStoredSession = () => {
      ['token', 'userId', 'firstAccess', 'cliente', 'idSessao', 'userEmail', 'username']
        .forEach(key => localStorage.removeItem(key));
    };

    const restoreSession = async () => {
      if (!token) {
        if (isActive) setIsAuthChecked(true);
        return;
      }

      try {
        const response = await fetch('/api/auth/session', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data.user?.id) {
          throw new Error(data.message || 'Sessão inválida');
        }

        if (!isActive) return;
        const { user } = data;
        localStorage.setItem('userId', String(user.id));
        localStorage.setItem('userEmail', user.email || '');
        localStorage.setItem('username', user.username || '');
        localStorage.setItem('firstAccess', String(!!user.firstAccess));
        localStorage.setItem('cliente', String(user.cliente));
        setIsAuthenticated(true);
        setUserId(user.id);
        setCliente(user.cliente);
        setRenewDate(user.renewDate);
        setDescriptionCliente(user.description);

        fetch('/api/access', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }).catch(() => {});
      } catch (_) {
        clearStoredSession();
        if (isActive) {
          setIsAuthenticated(false);
          setUserId(null);
          setCliente(0);
        }
      } finally {
        if (isActive) setIsAuthChecked(true);
      }
    };

    restoreSession();

    const newWebSocketManager = new WebSocketManager(handleWebSocketMessage, handleWebSocketError);
    setWebSocketManager(newWebSocketManager);

    return () => {
      isActive = false;
      if (newWebSocketManager) {
        newWebSocketManager.close();
      }
    };
  }, [handleWebSocketMessage, handleWebSocketError]);

  useEffect(() => {
    if (cliente === 0 && isAuthenticated) {
      onOpen(); // Open the modal
      // After 5 seconds, close modal and logout
      setTimeout(() => {
        onClose();
        handleLogout();
      }, 5000);
    }
  }, [cliente, isAuthenticated]);


  useEffect(() => {
    const fetchStocksWithOptionsAndTransactions = async () => {
      try {
        const data = await getAtivosOptions();
        const symbols = data?.map(stock => `${stock.symbol}|S`) || []; // Adicionando |S aos símbolos
        const symbolsT = data?.map(stock => stock.symbol) || []; // Adicionando |S aos símbolos

        // Apenas os símbolos com has_options = 1
        const symbolsWithOptions = data
          ?.filter(stock => stock.has_options === 1)
          .map(stock => stock.symbol) || [];
        setTickersWithOptions(symbolsWithOptions);


        setStockSymbols(symbols);
        setStocksWithOptions(symbols);
        setTickers(symbolsT);

        if (webSocketManager) {
          webSocketManager.subscribeToSymbols(symbols);
        }

        try {
          const allTransactions = await getAllTransactions();
          setTransactions(allTransactions);
        } catch (txErr) {
          console.warn('Erro ao carregar transações:', txErr.message);
          setTransactions([]);
        }
      } catch (error) {
        toast({
          title: translations[language].stockError,
          description: translations[language].stockErrorDescription,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    if (webSocketManager && userId) {
      fetchStocksWithOptionsAndTransactions();
    }
  }, [toast, userId, webSocketManager, language]);


  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await getUser(userId);
        setUserInfo(userData);
      } catch (error) {
        toast({
          title: translations[language].stockError,
          description: translations[language].stockErrorDescription,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    if (userId) {
      fetchUser();
    }
  }, [userId, toast, language]);

  useEffect(() => {
    // Só inicializa se já houver consentimento salvo; a primeira escolha é tratada no banner.
    if (initializeAnalytics()) {
      trackPageView();
    }
  }, []);

  const handleSelectStock = async (stock) => {
    console.log("Selecting stock:", stock); // Debug log
    setSelectTicker(null);
    setSelectedStock(stock);
    try {
        const stockSymbol = typeof stock === 'object' ? stock.symbol : stock;
        
        // Registra log de seleção de ação
        await createUserLog({
          id_usuario: userId,
          componente: 'Stock',
          acao: 'selecao',
          caminho: window.location.pathname,
          id_sessao: localStorage.getItem('idSessao'),
          detalhes: stockSymbol
        });

        // Usa stockSymbol para as chamadas de API
        await fetchPriceTicker(stockSymbol);

        if (hasOption === 1) {
          const optionsData = await fetchOptionsData(stockSymbol);
          setOptionData(optionsData || []);
        } else {
          setOptionData([]);
        }

        // Usa stockSymbol para buscar transações
        const transactionsData = await getTransactionsSymbol(stockSymbol);
        setTransactions(transactionsData);

    } catch (error) {
        console.error("Error in handleSelectStock:", error);
        toast({
            title: translations[language].transactionError,
            description: translations[language].transactionErrorDescription,
            status: "error",
            duration: 3000,
            isClosable: true,
        });
    } finally {
        setLoadingOptions(false);
    }
};

  const handleTabChange = async (newTab) => {
    setSelectedTab(newTab);
    try {
      await createUserLog({
        id_usuario: userId,
        componente: 'Tab',
        acao: 'mudanca',
        caminho: window.location.pathname,
        id_sessao: localStorage.getItem('idSessao'),
        detalhes: newTab
      });
    } catch (error) {
      console.error('Erro ao registrar mudança de aba:', error);
    }
  };

  useEffect(() => {
    if (tickersWithOptions.length > 0) {
        // Only set default stock if both selectedStock and selectTicker are null
        if (!selectedStock && !selectTicker) {
            console.log("Setting initial stock"); // Debug log
            const initialStock = tickersWithOptions[0];
            setSelectedStock(initialStock);
            handleSelectStock(initialStock);
        }
    }
}, [tickersWithOptions]); // Remove dependencies that cause unwanted updates


  const handleSelectTicker = async (stock) => {
    console.log("Selecting ticker:", stock); // Debug log
    setSelectedStock(null);
    setSelectTicker(stock);
    try {
        if (hasOption === 1) {
            setLoadingOptions(true);
            const optionsData = await fetchOptionsData(stock);
            setOptionData(optionsData || []);
        } else {
            setOptionData([]);
        }
    } catch (error) {
        console.error("Error in handleSelectTicker:", error);
        setOptionData([]);
    } finally {
        setLoadingOptions(false);
    }
};

  const fetchPriceTicker = async (symbol) => {
    try {
      const response = await fetch(`/api/cotacao/${symbol}`);
      if (!response.ok) {
        throw new Error(`Erro na API: status ${response.status}`);
      }
      const data = await response.json();
      if (data && data.price) {
        setLastPrice(parseFloat(data.price));
        setHasOption(data.has_options);
      }
    } catch (error) {
      console.error("Erro ao buscar cotação:", error.message);
    }
  };


  const fetchOptionsData = async (symbol) => {
    try {
      const response = await chamadaRemovida();
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      if (!text) {
        throw new Error('Response is empty');
      }

      // Converte o texto em um array de linhas e depois mapeia cada linha para um objeto
      const newOpcoes = text.split('\n').filter(Boolean).map(line => {
        const [
          symbol,
          value,
          variation,
          bid,
          ask,
          mid,
          financialVol,
          due_date,
          category,
          strike,
          days_to_maturity,
          contract_size,
          theoretical,
          delta
        ] = line.split('|'); // Separa os dados pelo delimitador "|"


        return {
          symbol,
          value: parseFloat(value),
          variation: parseFloat(variation),
          bid: parseFloat(bid),
          ask: parseFloat(ask),
          mid: parseFloat(mid),
          financialVol: parseFloat(financialVol),
          due_date,
          category,
          strike: parseFloat(strike),
          days_to_maturity: parseInt(days_to_maturity, 10),
          contract_size: parseFloat(contract_size),
          theoretical: parseFloat(theoretical),
          delta: parseFloat(delta),

        };
      });

      // Encontrar a data mais próxima de vencimento
      const sortedOptions = newOpcoes.sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
      const nearestDate = sortedOptions.length > 0 ? sortedOptions[0].due_date : null;
      setNearestDueDate(nearestDate);  // Atualiza o estado com a data mais próxima


      // Obter as datas de vencimento distintas
      const distinctDates = [...new Set(newOpcoes.map(option => option.due_date))];
      setDistinctDueDates(distinctDates);  // Atualiza o estado com as datas distintas

      return newOpcoes;


    } catch (error) {
      console.error("Error fetching options data:", error);
      setOptionData([]); // Define optionData como um array vazio em caso de erro

    }
  };


  const handleAddOrUpdateTransaction = useCallback(async (transaction) => {
    try {
      let updatedTransaction;
      if (transaction.id) {
        updatedTransaction = await updateTransaction(transaction.id, transaction);
      } else {
        updatedTransaction = await createTransaction(transaction);
      }

      setTransactions(prevTransactions => {
        const newTransactions = transaction.id
          ? prevTransactions.map(t => t.id === updatedTransaction.id ? updatedTransaction : t)
          : [...prevTransactions, updatedTransaction];
        return newTransactions;
      });

      toast({
        title: transaction.id ? translations[language].transactionUpdated : translations[language].transactionAdded,
        description: translations[language].transactionSuccessDescription,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      await createUserLog({
        id_usuario: userId,
        componente: 'Transacao',
        acao: transaction.id ? 'atualizacao' : 'criacao',
        caminho: window.location.pathname,
        id_sessao: localStorage.getItem('idSessao'),
        detalhes: JSON.stringify(transaction)
      });

    } catch (error) {
      toast({
        title: translations[language].transactionError,
        description: translations[language].transactionErrorDescription,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  }, [userId, toast, language]);


  const handleDeleteTransaction = async (transaction) => {
    try {
      await deleteTransaction(transaction.id);
      setTransactions(prevTransactions => prevTransactions.filter(t => t.id !== transaction.id));
      toast({
        title: translations[language].transactionDeleted,
        description: translations[language].transactionDeletedDescription,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: translations[language].transactionError,
        description: translations[language].transactionErrorDescription,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleLogout = async () => {
    try {
      await createUserLog({
        id_usuario: userId,
        componente: 'Autenticacao',
        acao: 'logout',
        caminho: window.location.pathname,
        id_sessao: localStorage.getItem('idSessao')
      });
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('cliente');
      localStorage.removeItem('idSessao');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('username');

      setIsAuthenticated(false);
      setUserId(null);
      if (webSocketManager) {
        webSocketManager.close();
      }
      toast({
        title: translations[language].logout,
        description: translations[language].logoutMessage,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePasswordResetComplete = () => {
    window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
    ['token', 'userId', 'firstAccess', 'cliente', 'idSessao', 'userEmail', 'username']
      .forEach(key => localStorage.removeItem(key));
    setPasswordResetToken('');
    setPasswordResetComplete(true);
    setShowLogin(true);
    setIsAuthenticated(false);
    setUserId(null);
    setCliente(0);
    if (webSocketManager) webSocketManager.close();
  };

  const handleDueDateChange = (dueDate) => {
    setDueDateFilter(dueDate);

    const filtered = optionData.filter(option => option.due_date === dueDate);

  };

  useEffect(() => {
    if (isAuthenticated && userId) {
      createUserLog({
        id_usuario: userId,
        componente: 'Pagina',
        acao: 'carregamento',
        caminho: window.location.pathname,
        id_sessao: localStorage.getItem('idSessao')
      });
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    let timer;
    if (isOpen) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isOpen]);

  if (!isAuthChecked) {
    return <div>Loading...</div>;
  }

  if (passwordResetToken) {
    return (
      <Login
        onLogin={handleLogin}
        onBack={() => {
          window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
          setPasswordResetToken('');
          setShowLogin(true);
        }}
        onResetComplete={handlePasswordResetComplete}
        resetToken={passwordResetToken}
      />
    );
  }

  if (currentHash === '#admin') {
    if (!isAuthenticated) {
      return <LandingPage onLogin={() => setShowLogin(true)} />;
    }
    return <AdminPanel
      onExit={() => { window.location.hash = ''; setCurrentHash(''); }}
    />;
  }

  if (!isAuthenticated) {
    if (showLogin) {
      return (
        <Login
          onLogin={handleLogin}
          onBack={() => setShowLogin(false)}
          initialMessage={passwordResetComplete ? 'Senha atualizada. Entre com sua nova senha para acessar a plataforma.' : ''}
        />
      );
    }
    return <LandingPage onLogin={() => setShowLogin(true)} />;
  }


  return (
    <ChakraProvider theme={theme}>
      <div className="app-container app-container--authenticated">
          {window.location.pathname.match(/^\/opcoes\/[^/]+$/) ? (
            <OptionsGridPage ticker={decodeURIComponent(window.location.pathname.split('/').pop()).toUpperCase()} />
          ) : (
            <KairiDashboard userId={userId} onLogout={handleLogout} />
          )}

        {showOnboarding && (
          <OnboardingWizard
            email={onboardingEmail}
            onComplete={() => setShowOnboarding(false)}
          />
        )}


        <Modal isOpen={loadingOptions} onClose={() => { }} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Carregando Opções</ModalHeader>
            <ModalCloseButton />
            <ModalBody display="flex" justifyContent="center" alignItems="center">
              <Spinner size="xl" />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
          <ModalOverlay />
          <ModalContent bg="gray.800">
            <ModalHeader color="white">Licença Expirada</ModalHeader>
            <ModalBody>
              <Text color="gray.100">
                Sua licença do Fatcat expirou. Para continuar tendo acesso a todas as funcionalidades, 
                por favor renove sua assinatura.
              </Text>
              {renewDate && (
                <Text color="gray.100" mt={2}>
                  Data de expiração: {new Date(renewDate).toLocaleDateString()}
                </Text>
              )}
              <Text color="gray.100" mt={4}>
                Você será redirecionado para a tela de login em {countdown} segundos...
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                mr={3}
                onClick={() => window.open('https://pay.kiwify.com.br/qGNnjXV', '_blank')}
              >
                Renovar Agora
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </ChakraProvider>
  );
}

const AppComConsentimento = () => (
  <>
    <App />
    <ConsentimentoCookies />
  </>
);

export default AppComConsentimento;